<template>
  <div id="map-container" class="columns is-gapless qa-map" style="margin-bottom: 0">
    <gmap-map
        ref="googleMap"
        :center="mapCenter"
        @click="onMapClicked"
        :zoom="9"
        :heading="heading"
        :options="options"
        map-type-id="terrain"
        style="width: 100%"
        class="mapbox-container">

      <gmap-info-window :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen"
                        @closeclick="onCloseInfoWindow">
      </gmap-info-window>

      <my-google-marker v-for="(item, index) in markers" :key="item._id"
                        :item="item"
                        @onMarkerClicked="onMarkerClicked(item, index)"
                        :is-selected="selectedSiteId === item._id"
                        :draw-mode="null">
      </my-google-marker>
    </gmap-map>
  </div>
</template>

<script>
import MyGoogleMarker from "@/modules/projects-module/components/my-google-marker/my-google-marker.vue";
import {gMapStyles} from "@/utils/utils";
import {bbox, lineString} from "@turf/turf";
import {mapGetters} from "vuex";

export default {
  name: "g-map-blurring",
  components: {MyGoogleMarker},
  data() {
    return {
      mapCenter: {lat: -37.80193, lng: 144.94444},
      heading: 0,
      options: {styles: gMapStyles},
      infoWindowPos: null,
      infoWinOpen: false,
      currentMidx: null,
      selectedSiteId: null,
      infoOptions: {
        content: '',
        pixelOffset: {
          width: 0,
          height: -16
        }
      }
    }
  },
  async mounted() {
    await this.$gmapApiPromiseLazy();

    this.$refs.googleMap.$mapObject.addListener('projection_changed', () => {
      this.heading = this.$refs.googleMap.$mapObject.heading
    });
    this.zoomToMarkers(true)
  },
  methods: {
    onCloseInfoWindow() {
      this.infoWinOpen = false
      this.selectedSiteId = null
    },
    onMapClicked() {
      this.$emit('onMapClicked')
      this.selectedSiteId = null
      if (!this.infoWinOpen) {
        this.zoomToMarkers(true)
      }
      this.infoWinOpen = false
    },
    onMarkerClicked(site, index) {
      if (this.selectedSiteId === site._id) {
        this.selectedSiteId = null
      } else {
        this.selectedSiteId = site._id
      }

      this.toggleInfoWindow(site, index)
      this.zoomToMarkers(true, site)
    },
    toggleInfoWindow(site, idx) {
      this.infoWindowPos = {lat: site.location.coordinates[1], lng: site.location.coordinates[0]};
      this.setInfoWindowContent(site)
      if (this.currentMidx === idx) {
        this.infoWinOpen = !this.infoWinOpen;
      } else {
        this.infoWinOpen = true;
        this.currentMidx = idx;
      }
    },
    setInfoWindowContent(site) {
      let taskStatus = 'No tasks'
      let statusColor = '#888888'
      if (site.tasks) {
        taskStatus = site.tasks.status
        statusColor = this.siteStatusColor(site)
      }
      this.infoOptions.content = `
        <div style="padding: 8px; min-width: 200px">
          <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px">${site.name}</div>
          <div style="display: flex; align-items: center; margin-top: 6px">
            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${statusColor}; margin-right: 6px"></span>
            <span style="font-size: 12px">${taskStatus}</span>
          </div>
          ${site.tasks && site.tasks.numOfTasks ? `<div style="font-size: 11px; color: #666; margin-top: 4px">Tasks: ${site.tasks.completed || 0}/${site.tasks.numOfTasks} completed</div>` : ''}
        </div>`
    },
    zoomToMarkers(animate, site) {
      if (!this.$refs.googleMap || !this.$refs.googleMap.$mapObject) {
        return
      }
      if (this.selectedProject != null) {
        let coordinates = []
        if (site && site.location) {
          coordinates.push(site.location.coordinates)
        } else if (this.markers && this.markers.length > 0) {
          this.markers.forEach(m => {
            if (m.location) {
              coordinates.push(m.location.coordinates)
            }
          })
        }

        if (coordinates.length > 0) {
          if (coordinates.length > 1) {
            let line = lineString(coordinates);
            let bb = bbox(line);
            let boundsGMap = new window.google.maps.LatLngBounds()
            boundsGMap.extend({lat: bb[1], lng: bb[0]})
            boundsGMap.extend({lat: bb[3], lng: bb[2]})
            this.$refs.googleMap.$mapObject.panTo(boundsGMap.getCenter());
            this.$refs.googleMap.$mapObject.fitBounds(boundsGMap, 100)
          } else {
            this.$refs.googleMap.$mapObject.panTo({lat: coordinates[0][1], lng: coordinates[0][0]});
          }
        } else if (animate) {
          this.$refs.googleMap.$mapObject.panTo(this.mapCenter);
        }
      } else {
        this.$refs.googleMap.$mapObject.panTo(this.mapCenter);
      }
    },
    siteStatusColor(site) {
      if (site.tasks) {
        if (site.tasks.status === 'COMPLETED') {
          return '#1CAB80'
        } else if (site.tasks.status === 'RUNNING') {
          return '#D8BA13'
        } else if (site.tasks.status === 'CREATED') {
          return '#8C84FF'
        } else if (site.tasks.status === 'FAILED') {
          return '#e02c2c'
        }
      }
      return '#888888'
    },
    shortName(name) {
      if (!name) return ''
      let parts = name.split(' - ')
      if (parts.length > 1) {
        return parts[1].substring(0, 12)
      }
      return name.substring(0, 12)
    }
  },
  computed: {
    ...mapGetters({
      selectedProject: 'dtpBlurringVideosModule/getSelectedBlurringProject',
    }),
    markers() {
      let sites = []
      if (this.selectedProject && this.selectedProject.sites) {
        this.selectedProject.sites.forEach(site => {
          if (site.location) {
            sites.push({
              ...site,
              objectType: 'site',
              color: this.siteStatusColor(site),
              shortName: this.shortName(site.name)
            })
          }
        })
      }
      return sites
    }
  },
  watch: {
    selectedProject: {
      handler: function (newVal, oldVal) {
        if ((oldVal && newVal === null) || (oldVal === null && newVal)) {
          this.zoomToMarkers(true)
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
.qa-map {
  height: 480px;
  transition: all .6s linear;
}
</style>
